import { Button } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ConfirmedIcon, CancelledIcon } from '@/common/components/icons';
import { BookingCalendarEventStatus } from '@/common/api/wisoryApiClient';
import { BookingSessionViewModel, formatDate } from '@/common/utils';
import { trackEvent } from '@/common/services/tracking';
import { CompanyNameWithDescription } from '@/common/components/company-name-with-description';
import styles from './styles.module.scss';

export enum UpcomingSessionStatuses {
  PENDING,
  VERIFIED,
  PERFORMED,
  CANCELLED,
}

type Props = {
  item: BookingSessionViewModel;
  className?: string;
  isAdvisor: boolean;
  status: BookingCalendarEventStatus;
  onChargeableClick?: VoidFunction;
  onRebuyBtnClick?: (s: BookingSessionViewModel) => void;
  onSelect?: (session: BookingSessionViewModel) => void;
};

export function UpcomingSessionItem({
  item,
  status,
  className = '',
  isAdvisor,
  onRebuyBtnClick,
  onChargeableClick,
  onSelect,
}: Props) {
  const { t } = useTranslation();

  const isVerified = status === BookingCalendarEventStatus.VERIFIED;
  const isCancelled = status === BookingCalendarEventStatus.CANCELLED;
  const isNoShow = status === BookingCalendarEventStatus.NO_SHOW;
  const isLateCancelled = status === BookingCalendarEventStatus.LATE_CANCELLED;
  const isPerformed =
    status === BookingCalendarEventStatus.PERFORMED ||
    status === BookingCalendarEventStatus.INVOICED;
  const isAwaitingProposal = [
    BookingCalendarEventStatus.USER_SENT_PROPOSAL,
    BookingCalendarEventStatus.ADVISOR_SENT_PROPOSAL,
    BookingCalendarEventStatus.ADMIN_SENT_PROPOSAL_TO_USER,
    BookingCalendarEventStatus.ADMIN_SENT_PROPOSAL_TO_ADVISOR,
  ].includes(status);

  const bookingId = item.id;
  const isRefunded = item.isRefunded || false;

  return (
    <div
      className={classNames(styles.item, className, {
        [styles.confirmed]: isVerified || isPerformed,
        [styles.lateCancelled]: isLateCancelled || isNoShow,
        [styles.cancelled]: isCancelled || isAwaitingProposal,
      })}
      data-dd-privacy="mask"
    >
      <div className={styles.statusColumn}>
        <div className={styles.info}>{formatDate(item.startTime, 'DD MMM, HH.mm')}</div>
        <div className={styles.icon}>
          {isVerified && (
            <>
              <div className={styles.iconWrapper}>
                <ConfirmedIcon />
              </div>
              <span>{t('home.confirmed')}</span>
            </>
          )}
          {isPerformed && (
            <>
              <div className={styles.iconWrapper}>
                <ConfirmedIcon />
              </div>
              <span>{t('home.performed')}</span>
            </>
          )}
          {(isCancelled || isLateCancelled || isAwaitingProposal) && (
            <>
              <div className={styles.iconWrapper}>
                <CancelledIcon />
              </div>
              <span>
                {isAwaitingProposal
                  ? t('home.awaitingProposalResponse')
                  : t(isCancelled ? 'home.cancelled' : 'home.lateCancelled')}
              </span>
            </>
          )}

          {isNoShow && onChargeableClick && (
            <>
              <span>{t('home.noShow')}</span>
            </>
          )}
        </div>
      </div>
      <div className={styles.contentColumn}>
        {isAdvisor && (
          <>
            <div className={styles.name}>{item.customerFullName}</div>
            <div className={styles.company}>
              <CompanyNameWithDescription
                companyName={item.customerCompanyName}
                companyId={item.companyId}
              />
            </div>
          </>
        )}

        {!isAdvisor && (
          <>
            <div className={styles.name}>{item.advisorName}</div>
          </>
        )}
      </div>

      <div className={styles.actionColumn}>
        {onSelect && (
          <Button
            className={classNames({
              [styles.hasBookingId]: !!bookingId,
              [styles.hasFeedbackBtn]: isAdvisor && onRebuyBtnClick,
            })}
            onClick={() => {
              if (isCancelled || isLateCancelled || isAwaitingProposal) {
                trackEvent('User_View_SessionDetails_CancelledSession', 'BOOKING DETAIL');
              }

              onSelect?.(item);
            }}
          >
            {t('home.viewDetails')}
          </Button>
        )}

        {bookingId && (
          <div className={styles.bookingIdWrapper}>
            (ID {bookingId})
            {!isRefunded && !isPerformed && !isVerified && isAdvisor && onChargeableClick && (
              <span
                role="button"
                className={styles.chargeable}
                onClick={() => {
                  onChargeableClick();
                }}
              >
                {t('home.chargeable')}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
